/**
 * The markup and the 3D of the desktop album preview (design handoff "Aperçu 3D de l'album") : the
 * album lying on a wooden table, its leaves turning in 3D around the binding, seen from above
 * ("Vue de face") or tilted ("Vue perspective"), always centred — the cover alone when closed on
 * the front, the spread when open, the back alone when closed on the back.
 *
 * A leaf carries two faces, its recto and its verso : leaf i shows faces 2i and 2i + 1, and at
 * spread c (0 … leaves) the spread on the table is faces 2c - 1 | 2c. Turning a leaf is a −180°
 * rotation.
 *
 * The album has the thickness of what it is made of (desktop.common.preview.BookBinding) : each
 * side is a pile of sheets laid at their real height, the edges of the sheets hidden from view
 * drawn by a block under the ones showing ; the first and last leaves are the covers, a board as
 * thick as the binding's, overhanging the pages on a hard cover, with a spine when the album is
 * closed. A spiral album has its two sides apart, turning round a coil of rings through the
 * holes punched in every page.
 *
 * Open, the two piles are not as high as each other : the higher one's sheets curve down to the
 * fold in a strip along it, as a thick album's do, and lie flat beyond. A sheet is a chain of
 * segments hinged on each other (SEGMENTS across the strip, then the rest of the page), each
 * carrying its slice of the recto and of the verso ; the covers are boards, and do not bend.
 *
 * Do not put overflow, filter or opacity < 1 on .fpTable, .fpAnchor, .fpBook or .fpLeaf : it
 * flattens the 3D (Safari first).
 *
 *   build(faces, binding) faces : [{ cover, plain }] in book order, binding : a BookBinding
 *   image(index, url, loading)  the picture of a face, once drawn ; loading : the photos still
 *                         loading are cut out of it, the face blinks through
 *   progress(index, value, zone)  the progress bar of a face still loading, value 0 … 1 ; zone : where
 *                         the photo loading lies, { cx, cy, width } shares of the page (its centre,
 *                         its width), the middle of the page without one
 *   texts(values)         any of title, subtitle, options, order
 *   go(spread)            next() / previous() / spread, the position : 0 the cover … leaves the back
 *   view                  "face" | "perspective"
 *
 * Triggers "spread" when the page changes, "back", "order" and "options" for the header's buttons.
 */
fx.ns("desktop.common.preview", class BookTable extends fx.Component {

    constructor(data)
    {
        super(data);

        this.spread = 0;
        this.leaves = 0;
        this._view = "face";              // the view the preview opens on
        this._ratio = 1.4;
        this._labelOf = () => "";

        this._onResizeHandler = this.fit.bind(this);
    }

    // base width of a page before scaling, px
    get PAGE_WIDTH() { return 440; }

    // the tilt of "Vue perspective", degrees
    get PERSPECTIVE() { return 21; }

    // leaves laid on each side of the spread : enough for the ones still turning after quick clicks
    get STACK() { return 3; }

    // the curve of a sheet down to the fold : its segments across the strip, and each one's angle
    // as a share of the first's — steep at the fold, flatter and flatter away from it
    get SEGMENTS() { return [1, 0.7, 0.4]; }

    get view()
    {
        return this._view;
    }

    set view(val)
    {
        this._view = (val == "perspective") ? "perspective" : "face";

        if (this.node)
        {
            this.fit();
            this._render();
        }
    }

    // a function (spread) → the label under the book : "Couverture", "Pages 2 – 3", "Dos"
    set labelOf(fn)
    {
        this._labelOf = fn;
        this._render();
    }

    // --------------------------- RENDER --------------------------- //

    create()
    {
        super.create();

        this.addClass("fp");
        this.attr("role", "region");
        this.attr("aria-label", fx.text("Aperçu de votre album"));
    }

    render()
    {
        return  '<div class="fpTable"><div class="fpAnchor"><div class="fpBook"><div class="fpShadow"></div></div></div></div>' +
                '<div class="fpVignette"></div>' +
                '<button type="button" class="fpHot fpHotPrevious" data-action="previous" tabindex="-1" aria-label="' + fx.text("Page précédente") + '"></button>' +
                '<button type="button" class="fpHot fpHotNext" data-action="next" tabindex="-1" aria-label="' + fx.text("Page suivante") + '"></button>' +
                '<header class="fpHead">' +
                    '<h1 class="fpTitle"></h1>' +
                    '<p class="fpSubtitle"><span class="fpSubtitleText"></span>' +
                        '<button type="button" class="fpOptions" data-action="options" title="' + fx.text("Cliquez pour changer la taille ou la couverture") + '">' + this._icon("edit") + '<span class="fpOptionsText"></span></button>' +
                    '</p>' +
                '</header>' +
                '<div class="fpActions">' +
                    '<button type="button" class="fpButton fpButtonLight" data-action="back">' + this._icon("back") + '<span>' + fx.text("Retour à l'éditeur") + '</span></button>' +
                    '<button type="button" class="fpButton fpButtonOrder" data-action="order" data-testid="preview-order"></button>' +
                '</div>' +
                '<nav class="fpNav" aria-label="' + fx.text("Pagination") + '">' +
                    '<button type="button" class="fpArrow" data-action="previous" aria-label="' + fx.text("Page précédente") + '">' + this._icon("previous") + '</button>' +
                    '<span class="fpLabel" aria-live="polite"></span>' +
                    '<button type="button" class="fpArrow" data-action="next" aria-label="' + fx.text("Page suivante") + '">' + this._icon("next") + '</button>' +
                '</nav>' +
                '<div class="fpViews" role="group" aria-label="' + fx.text("Vue") + '">' +
                    '<button type="button" class="fpView" data-action="view" data-view="face">' + fx.text("Vue de face") + '</button>' +
                    '<button type="button" class="fpView" data-action="view" data-view="perspective">' + fx.text("Vue perspective") + '</button>' +
                '</div>';
    }

    _icon(name)
    {
        let paths = {
            previous : '<path d="M19 12H5M11 18l-6-6 6-6"/>',
            next : '<path d="M5 12h14M13 6l6 6-6 6"/>',
            back : '<path d="M15 18l-6-6 6-6"/>',
            edit : '<path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4"/>',
        };
        let size = (name == "previous" || name == "next") ? 22 : 16;

        return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths[name] + '</svg>';
    }

    onReady()
    {
        super.onReady();

        this.node.style.setProperty("--fp-table-image", 'url("/app/assets/images/preview/oak.jpg")');

        this.on("click", this._onClick, this.node);
        this.on("pointerdown", this._onPointerDown, this.node);
        this.on("pointerup", this._onPointerUp, this.node);

        this.on("LEFT-DOWN", this.previous, fx.keyboard);
        this.on("RIGHT-DOWN", this.next, fx.keyboard);
        this.on("HOME-DOWN", this._onHome, fx.keyboard);
        this.on("END-DOWN", this._onEnd, fx.keyboard);

        if (typeof ResizeObserver != "undefined")
        {
            this._observer = new ResizeObserver(this._onResizeHandler);
            this._observer.observe(this.node);
        }
        else
            window.addEventListener("resize", this._onResizeHandler);

        this.fit();
        this._render();
    }

    onDisplayUpdated()
    {
        super.onDisplayUpdated();

        this.fit();
    }

    /**
     * Lays the leaves, every face blank until image() gives its picture.
     * @param {Array} faces : [{ cover : Boolean, plain : Boolean }], an even number, in book order
     * @param {desktop.common.preview.BookBinding} binding
     */
    build(faces, binding)
    {
        let book = this.find(".fpBook");

        this.findAll(".fpLeaf, .fpStack, .fpSpine, .fpCoil").forEach((node) => node.remove());

        this.binding = binding;
        this._ratio = binding.pageWidth / binding.pageHeight;
        this.leaves = faces.length / 2;
        this.spread = Math.min(this.spread, this.leaves);

        // millimetres to pixels of the page drawn PAGE_WIDTH wide ; a sheet never under a pixel's
        // fraction Chrome would sort wrongly
        let mm = this.PAGE_WIDTH / binding.pageWidth,
            g;
        this.geometry = {
            pw : this.PAGE_WIDTH,
            ph : Math.round(this.PAGE_WIDTH / this._ratio),
            sheet : Math.max(0.6, binding.sheet * mm) * binding.sheetShare((this.leaves - 2) * 2),
            cover : Math.max(0.6, binding.cover * mm),
            inset : binding.overhang * mm,
            gap : binding.gap * mm,
        };

        // the strip along the fold where a sheet curves : wide enough for the highest step between
        // the two piles (all the inner sheets on one side) to make a gentle curve, a thick album's
        // as soft as a thin one's, up to half the page
        g = this.geometry;
        g.strip = Math.min(g.pw * 0.55, Math.max(g.pw * 0.12, 3.5 * (Math.max(0, this.leaves - 2) * g.sheet + g.cover)));

        this.node.style.setProperty("--fp-pw", g.pw + "px");
        this.node.style.setProperty("--fp-ph", g.ph + "px");
        this.node.style.setProperty("--fp-inset", g.inset + "px");
        this.node.style.setProperty("--fp-half-gap", (g.gap / 2) + "px");
        this.node.style.setProperty("--fp-cover", g.cover + "px");
        this.node.style.setProperty("--fp-sheet", g.sheet + "px");
        this.toggleClass("fpSpiral", binding.spiral);
        this.toggleClass("fpHard", binding.hard);

        // the sides' blocks and the spine, under the leaves
        ["fpStackLeft", "fpStackRight"].forEach((side) => {
            let stack = document.createElement("div");
            stack.className = "fpStack " + side;
            stack.innerHTML = '<div class="fpStackOuter"></div><div class="fpStackSpine"></div><div class="fpStackNear"></div>';
            book.appendChild(stack);
        });

        if (!binding.spiral)
        {
            let spine = document.createElement("div");
            spine.className = "fpSpine";
            book.appendChild(spine);
        }

        for (let i = 0; i < this.leaves; i++)
        {
            let leaf = document.createElement("div"),
                cover = (i == 0 || i == this.leaves - 1);

            leaf.className = "fpLeaf" + (cover ? " fpCoverLeaf" : "");

            if (cover)
            {
                leaf.appendChild(this._face(faces[2 * i], false, 2 * i));
                leaf.appendChild(this._face(faces[2 * i + 1], true, 2 * i + 1));
            }
            else
                leaf.appendChild(this._segments(faces[2 * i], faces[2 * i + 1], 2 * i));

            // the board's edges, from the face down to the inside one
            if (cover)
                leaf.insertAdjacentHTML("beforeend", '<div class="fpEdge fpEdgeOuter"></div><div class="fpEdge fpEdgeTop"></div><div class="fpEdge fpEdgeBottom"></div>');

            book.appendChild(leaf);
        }

        if (binding.spiral)
            book.appendChild(this._coil(mm));

        this.fit();
        this._render();
    }

    // the spiral : a ring through each pair of holes, standing across the gap, and the top of its
    // loop, which is what shows from above
    _coil(mm)
    {
        let coil = document.createElement("div"),
            g = this.geometry,
            width = g.gap + 2 * this.binding.holeOffset * mm;

        coil.className = "fpCoil";
        coil.innerHTML = this.binding.holeCentres.map((y) =>
            '<div class="fpRing" style="left:' + (g.pw - width / 2) + 'px;top:' + (y * mm) + 'px;width:' + width + 'px"></div>' +
            '<div class="fpWire" style="left:' + (g.pw - width / 2) + 'px;top:' + (y * mm - 1.5) + 'px;width:' + width + 'px"></div>'
        ).join("");

        this._ringWidth = width;
        return coil;
    }

    /**
     * A sheet as its chain of segments, from the fold outwards : each nested in the one before, at
     * its outer edge, turning on it by the angle of its CSS variable (--fp-b0 …, set by _layLeaves).
     * Each carries the slice of the recto over its width, and the verso's matching slice.
     */
    _segments(recto, verso, index)
    {
        let g = this.geometry,
            widths = this.SEGMENTS.map(() => g.strip / this.SEGMENTS.length).concat([g.pw - g.strip]),
            first = null,
            parent = null,
            x = 0;

        widths.forEach((width, k) => {
            let segment = document.createElement("div");
            segment.className = "fpSegment";
            segment.style.width = width + "px";
            // the flat rest of the page stretched to reach as far as a flat sheet (_bend)
            segment.style.transform = "rotateY(var(--fp-b" + k + ", 0deg))" + (k == widths.length - 1 ? " scaleX(var(--fp-stretch, 1))" : "");

            // the recto's slice from x, the verso's mirrored : the verso is the page seen from behind.
            // Past the first, a slice reaches four pixels back over the one before (fpOverlap), or
            // the joints show as hairlines : the recto then starts that much earlier in the page,
            // the verso, turned round, keeps its offset
            let overlap = k ? 4 : 0,
                front = this._face(recto, false, index, x - overlap),
                back = this._face(verso, true, index + 1, g.pw - x - width);

            front.classList.toggle("fpOverlap", !!overlap);
            back.classList.toggle("fpOverlap", !!overlap);
            segment.appendChild(front);
            segment.appendChild(back);

            if (parent)
                parent.appendChild(segment);
            else
                first = segment;

            parent = segment;
            x += width;
        });

        return first;
    }

    // a face, or its slice from offset (pixels of the page) when the sheet is in segments
    _face(face, verso, index, offset)
    {
        let node = document.createElement("div");
        node.className = "fpFace" + (verso ? " fpVerso" : "") + (face.cover ? " fpCover" : "") + (face.plain ? " fpPlain" : " fpWaiting") + (offset !== undefined ? " fpSlice" : "");
        node.dataset.face = index;

        if (offset !== undefined)
        {
            node.dataset.offset = offset;
            node.style.setProperty("--fp-offset", offset + "px");
        }

        return node;
    }

    // loading : the picture is incomplete, the photos still loading cut out of it blink
    image(index, url, loading)
    {
        this.findAll('.fpFace[data-face="' + index + '"]').forEach((face) => {
            let img = face.querySelector("img") || document.createElement("img");
            img.decoding = "async";
            img.draggable = false;
            img.alt = "";
            img.src = url;

            // a slice shows the page from its offset
            if (face.dataset.offset !== undefined)
                img.style.left = -parseFloat(face.dataset.offset) + "px";

            if (!img.parentNode)
                face.appendChild(img);

            face.classList.remove("fpWaiting");
            face.classList.add("fpDrawn");
            face.classList.toggle("fpLoading", !!loading);

            // the cover's board is wrapped in its print : its edges take the colour of the cover's
            if (index == 0)
                img.onload = () => this._coverEdgeColour(img);
        });
    }

    progress(index, value, zone)
    {
        let g = this.geometry;
        if (!g)
            return;

        // the bar : at most a third of the page, half the photo's width
        let width = zone ? Math.min(g.pw * 0.36, zone.width * g.pw * 0.5) : g.pw * 0.36,
            x = (zone ? zone.cx * g.pw : g.pw / 2) - width / 2,
            y = (zone ? zone.cy * g.ph : g.ph / 2) - 2;

        this.findAll('.fpFace[data-face="' + index + '"]').forEach((face) => {
            face.style.setProperty("--fp-progress", Math.max(0, Math.min(1, value || 0)).toFixed(3));
            face.style.setProperty("--fp-bar-x", x.toFixed(1) + "px");
            face.style.setProperty("--fp-bar-y", y.toFixed(1) + "px");
            face.style.setProperty("--fp-bar-width", width.toFixed(1) + "px");
        });
    }

    _coverEdgeColour(img)
    {
        try {
            let canvas = document.createElement("canvas"),
                context = canvas.getContext("2d"),
                width = img.naturalWidth,
                height = img.naturalHeight;

            canvas.width = 4;
            canvas.height = 32;
            context.drawImage(img, width - 4, 0, 4, height, 0, 0, 4, 32);

            let data = context.getImageData(0, 0, 4, 32).data,
                sum = [0, 0, 0];

            for (let i = 0; i < data.length; i += 4)
                for (let k = 0; k < 3; k++)
                    sum[k] += data[i + k];

            let colour = sum.map((v) => Math.round(v / (data.length / 4) * 0.8));      // darker : the edge is in shade
            this.node.style.setProperty("--fp-cover-edge", "rgb(" + colour.join(",") + ")");
        }
        catch (e) {}
    }

    texts(values)
    {
        let text = (selector, value) => {
            let node = this.find(selector);
            if (node && value !== undefined)
                node.textContent = value;
        };

        text(".fpTitle", values.title);
        text(".fpSubtitleText", values.subtitle);
        text(".fpOptionsText", values.options);
        text(".fpButtonOrder", values.order);
    }

    // --------------------------- POSITION AND VIEW --------------------------- //

    go(spread)
    {
        spread = Math.max(0, Math.min(this.leaves, spread));
        if (spread == this.spread)
            return;

        this.spread = spread;
        this._render();

        this.trigger("spread", { spread : spread });
    }

    next()
    {
        this.go(this.spread + 1);
    }

    previous()
    {
        this.go(this.spread - 1);
    }

    _onHome()
    {
        this.go(0);
    }

    _onEnd()
    {
        this.go(this.leaves);
    }

    _render()
    {
        if (!this.node)
            return;

        let c = this.spread,
            n = this.leaves,
            pw = this.PAGE_WIDTH,
            single = (c == 0 || c == n);

        this._layLeaves();

        this.node.style.setProperty("--fp-tilt", (this._view == "perspective" ? this.PERSPECTIVE : 0) + "deg");

        let g = this.geometry || { pw : pw, ph : 0, inset : 0, gap : 0 },
            half = g.gap / 2;

        // closed, the cover alone is centred ; a spiral's coil is out of it
        this.node.style.setProperty("--fp-shift", (c == 0 ? -(pw + g.gap) / 2 : (c == n ? (pw + g.gap) / 2 : 0)) + "px");

        // the shadow under the album : its outline, without a page over it, reads as a ghost page.
        // It widens once the leaf has landed (opening the cover, leaving the back) and narrows at
        // once (closing on the cover or on the back), never while the leaf is in the air
        let shadow = this.find(".fpShadow"),
            width = (single ? pw : pw * 2 + g.gap) + (single ? g.inset : 2 * g.inset),
            widens = width > (parseFloat(shadow.style.width) || 0);

        shadow.classList.toggle("fpWidens", widens);
        shadow.style.left = (c == 0 ? pw + half : -half - g.inset) + "px";
        shadow.style.width = width + "px";
        shadow.style.top = -g.inset + "px";
        shadow.style.height = (g.ph + 2 * g.inset) + "px";

        this.find(".fpLabel").textContent = this._labelOf(c);

        this.findAll('[data-action="previous"]').forEach((button) => button.disabled = (c == 0));
        this.findAll('[data-action="next"]').forEach((button) => button.disabled = (c == n));
        this.findAll(".fpView").forEach((button) => button.setAttribute("aria-pressed", button.dataset.view == this._view));
    }

    /**
     * Every leaf at its height in its pile, and the piles' blocks. Bottom to top, the right pile is
     * the last leaf (the back cover) up to the spread's, the left one the first leaf (the front
     * cover) up to the spread's : a leaf on the right hangs its thickness under its plane, a turned
     * one stands it over its plane, so that a board turning keeps its inside face up.
     *
     * Only the leaves near the top of each pile are shown, and the covers : the others are the
     * block's edges.
     */
    _layLeaves()
    {
        let leaves = this.findAll(".fpLeaf");
        if (!leaves.length || !this.geometry)
            return;

        let c = this.spread,
            n = this.leaves,
            g = this.geometry,
            thickness = (i) => (i == 0 || i == n - 1) ? g.cover : g.sheet,
            plane = [],
            right = 0.5,
            left = 0.5;

        for (let i = n - 1; i >= c; i--)
        {
            right += thickness(i);
            plane[i] = right;
        }

        for (let i = 0; i < c; i++)
        {
            plane[i] = left;
            left += thickness(i);
        }

        // open, the higher pile's sheets curve down to the fold, level there with the other side's,
        // as a thick album's do towards its binding : laid flat, the two sides stepped at the fold.
        // Not a spiral's : its sheets lie flat on the coil, each side at its own height
        let open = c > 0 && c < n && !this.binding.spiral,
            dropRight = open ? Math.max(0, right - left) : 0,
            dropLeft = open ? Math.max(0, left - right) : 0,
            bendRight = this._bend(dropRight),
            bendLeft = this._bend(dropLeft);

        leaves.forEach((leaf, i) => {
            let turned = i < c,
                depth = turned ? c - 1 - i : i - c,
                shown = depth < this.STACK || i == 0 || i == n - 1,
                cover = (i == 0 || i == n - 1),
                drop = cover ? 0 : (turned ? dropLeft : dropRight),
                bend = turned ? bendLeft : bendRight;

            // the sheet's fold end at the other side's height, its curve rising by the drop to its
            // pile. Hidden sheets curve too : one coming into view flat would rise through the
            // others while it bends. Turned over, a sheet bends the other way round
            leaf.style.visibility = shown ? "" : "hidden";
            leaf.style.transform = "translateZ(" + (plane[i] - drop).toFixed(2) + "px) rotateY(" + (turned ? -180 : 0) + "deg)";

            if (!cover)
            {
                bend.turns.forEach((angle, k) => leaf.style.setProperty("--fp-b" + k, ((turned ? 1 : -1) * angle).toFixed(3) + "deg"));
                leaf.style.setProperty("--fp-stretch", bend.stretch.toFixed(4));
            }
        });

        // the blocks : the inner sheets of each side, over its cover when it lies there, under the
        // other cover when it lies on top (the album closed)
        let rightBottom = 0.5 + (c < n ? g.cover : 0),
            leftBottom = 0.5 + (c > 0 ? g.cover : 0);

        this._stack(".fpStackRight", rightBottom, right - (c == 0 ? g.cover : 0) - rightBottom, dropRight, bendRight, false);
        this._stack(".fpStackLeft", leftBottom, left - (c == n ? g.cover : 0) - leftBottom, dropLeft, bendLeft, true);

        // the spine, closed only, once the cover has landed
        let spine = this.find(".fpSpine");
        if (spine)
        {
            spine.classList.toggle("fpShown", c == 0 || c == n);
            spine.style.width = (c == 0 ? right : left) - 0.5 + "px";
        }

        let coil = this.find(".fpCoil");
        if (coil)
            coil.style.setProperty("--fp-ring-height", Math.max(this._ringWidth || 0, Math.max(right, left) + 4) + "px");
    }

    /**
     * The angles of a sheet's segments rising by drop across the strip, as relative turns : each
     * segment's angle to the one before it, the last one (the flat rest of the page) back to level.
     * A curved sheet reaches less far than a flat one : stretch is how much the flat rest of the
     * page widens to keep its outer edge on its pile's, which would otherwise stand out past it.
     * @return {Object} { turns : [degrees], stretch : Number }
     */
    _bend(drop)
    {
        let shares = this.SEGMENTS,
            length = this.geometry.strip / shares.length,
            rise = (a) => shares.reduce((sum, share) => sum + length * Math.sin(a * share), 0),
            low = 0,
            high = Math.PI / 2 * 0.98;

        if (drop <= 0.05)
            return { turns : shares.map(() => 0).concat([0]), stretch : 1 };

        for (let k = 0; k < 30; k++)
        {
            let mid = (low + high) / 2;
            if (rise(mid) < drop) low = mid; else high = mid;
        }

        let absolute = shares.map((share) => low * share * 180 / Math.PI).concat([0]),
            short = shares.reduce((sum, share) => sum + length * (1 - Math.cos(low * share)), 0),
            rest = this.geometry.pw - this.geometry.strip;

        return {
            turns : absolute.map((angle, k) => angle - (k ? absolute[k - 1] : 0)),
            stretch : (rest + short) / rest,
        };
    }

    /**
     * A side's block of sheets : bottom and height in pixels ; under sheets curving down to the
     * fold, its faces along the pages are cut to their curve (drop, bend), from the fold — on the
     * left for the right block, on the right for the left one (mirrored).
     */
    _stack(selector, bottom, height, drop, bend, mirrored)
    {
        let stack = this.find(selector);
        if (!stack)
            return;

        height = Math.max(0, height);
        drop = Math.min(height, drop || 0);

        stack.style.visibility = height > 0.3 ? "" : "hidden";
        stack.style.transform = "translateZ(" + bottom.toFixed(2) + "px)";
        stack.style.setProperty("--fp-stack", height.toFixed(2) + "px");
        stack.style.setProperty("--fp-stack-fold", (height - drop).toFixed(2) + "px");

        // the curve's points, fold first : x along the page, y up the face (its height, the top)
        let length = this.geometry.strip / this.SEGMENTS.length,
            x = 0,
            y = height - drop,
            angle = 0,
            points = [[0, y]];

        ((bend && bend.turns) || []).slice(0, this.SEGMENTS.length).forEach((turn) => {
            angle += turn * Math.PI / 180;
            x += length * Math.cos(angle);
            y += length * Math.sin(angle);
            points.push([x, Math.min(height, y)]);
        });

        let clip = drop > 0.05 ? "polygon(" + this._outline(points, height, this.geometry.pw, mirrored) + ")" : "none";
        stack.querySelectorAll(".fpStackNear").forEach((face) => face.style.clipPath = clip);
    }

    /**
     * The outline keeping a block face under the curve. The face, once stood up, has its y = 0 on
     * the table and y = height at the pile's top ; its x = 0 at the fold for the right block, at
     * the outer edge for the left one.
     */
    _outline(points, height, width, mirrored)
    {
        let px = (x, y) => x.toFixed(2) + "px " + y.toFixed(2) + "px",
            curve = points.map((p) => px(mirrored ? width - p[0] : p[0], p[1]));

        // the right block : table under the fold, the curve outwards, the top out to the outer edge,
        // down to the table there. The left one, the same seen in the mirror
        return mirrored ?
            [px(0, 0), px(0, height), px(width - points[points.length - 1][0], height)].concat(curve.slice().reverse()).concat([px(width, 0)]).join(", ") :
            [px(0, 0)].concat(curve).concat([px(width, height), px(width, 0)]).join(", ");
    }

    // the open album fits the screen, between the header and the arrows
    fit()
    {
        if (!this.node)
            return;

        let g = this.geometry || { gap : 0, inset : 0 },
            w = this.node.clientWidth,
            h = this.node.clientHeight,
            pw = this.PAGE_WIDTH,
            ph = pw / this._ratio,
            scale = Math.min((w - 120) / (pw * 2 + g.gap + 2 * g.inset), (h - 260) / (ph + 2 * g.inset), 1.6);

        // the scale of "Vue de face", whatever the view : how large a page can show (PageArtwork's resolution)
        this.faceScale = Math.max(0.3, scale);

        if (this._view == "perspective")
            scale *= 0.82;      // the tilt brings the front of the album closer

        this.node.style.setProperty("--fp-scale", Math.max(0.3, scale).toFixed(3));
    }

    // --------------------------- EVENTS --------------------------- //

    _onClick(e)
    {
        let button = e.target.closest ? e.target.closest("[data-action]") : null;
        if (!button || button.disabled || !this.node.contains(button))
            return;

        switch (button.dataset.action)
        {
            case "previous" : this.previous(); break;
            case "next" : this.next(); break;
            case "view" : this.view = button.dataset.view; break;
            default : this.trigger(button.dataset.action);
        }
    }

    // a swipe on a touch screen turns the page
    _onPointerDown(e)
    {
        this._swipeX = (e.pointerType != "mouse") ? e.clientX : null;
    }

    _onPointerUp(e)
    {
        if (this._swipeX == null)
            return;

        let dx = e.clientX - this._swipeX;
        this._swipeX = null;

        if (Math.abs(dx) > 40)
            this.go(this.spread + (dx < 0 ? 1 : -1));
    }

    findAll(selector)
    {
        return this.node ? Array.prototype.slice.call(this.node.querySelectorAll(selector)) : [];
    }

    destroy()
    {
        if (this._observer)
            this._observer.disconnect();
        else
            window.removeEventListener("resize", this._onResizeHandler);

        this._observer = null;

        super.destroy();
    }
});
